import React, { useRef, useState, useEffect } from "react";
import AttachIcon from "src/img/new/attach-image.svg";
import { ImagePreview, ThumbnailPlaceholderPreview } from "./NoticeMediaPreview";
import { VideoBottomControls, VideoContainer, 
    VideoControlsBar, ControlButton, 
    CustomRange, PauseIcon, 
    PlayIcon, formatTime,
    VolumeIcon, FullscreenIcon
} from "src/components";

/**
 * @param {string} postType
 * @param {NoticeImage|NoticeVideo|undefined} noticeMedia
 * @returns {Element}
 */
export const PostTypeAssetPreview = ({
    postType,
    noticeMedia,
    trimTimeStart,
    trimTimeEnd,
    crop,
    mediaIndex
}) => {
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(false);
    const containerRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(trimTimeStart);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const isImage = noticeMedia && (noticeMedia.mediaType() === "image" || (postType === "carousel" && noticeMedia.mediaType() === "image"));
    const isVideo = noticeMedia && (noticeMedia.mediaType() === "video" || (postType === "carousel" && noticeMedia.mediaType() === "video"));

    const videoSrc = noticeMedia?.getMediaAssetUrl?.();
    const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleLoadedMetadata = () => {
            const vidDuration = video.duration;
            const validTrimStart = trimTimeStart >= 0 && trimTimeStart < vidDuration ? trimTimeStart : 0;
            const validTrimEnd = trimTimeEnd > validTrimStart && trimTimeEnd <= vidDuration ? trimTimeEnd : vidDuration;
            setDuration(validTrimEnd - validTrimStart);
            video.currentTime = validTrimStart;
            setCurrentTime(validTrimStart);

            setVideoDimensions({
                width: video.videoWidth,
                height: video.videoHeight,
            });
            video.pause();
            setIsPlaying(false);
        };

        const handleTimeUpdate = () => {
            if (trimTimeEnd && video.currentTime >= trimTimeEnd) {
                video.pause();
                video.currentTime = trimTimeStart;
                setCurrentTime(trimTimeStart);
            } else {
                setCurrentTime(video.currentTime);
            }
        };
        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);

        video.addEventListener("loadedmetadata", handleLoadedMetadata);
        video.addEventListener("timeupdate", handleTimeUpdate);
        video.addEventListener("play", handlePlay);
        video.addEventListener("pause", handlePause);
        return () => {
            video.removeEventListener("loadedmetadata", handleLoadedMetadata);
            video.removeEventListener("timeupdate", handleTimeUpdate);
            video.removeEventListener("play", handlePlay);
            video.removeEventListener("pause", handlePause);
        };
    }, [videoSrc, trimTimeStart, trimTimeEnd]);

    const handleProgressChange = (e) => {
        const video = videoRef.current;
        if (!video) return;
        const validTrimStart = trimTimeStart >= 0 ? trimTimeStart : 0;
        const newTime = parseFloat(e.target.value) + validTrimStart;
        video.currentTime = newTime;
    };

    const togglePlayPause = () => {
        if (!videoRef.current) return;
        if (videoRef.current.paused) {
            videoRef.current.play();
        } else {
            videoRef.current.pause();
        }
    };

    const toggleMute = () => {
        if (!videoRef.current) return;
        videoRef.current.muted = !videoRef.current.muted;
        setMuted(videoRef.current.muted);
    };

    const toggleFullscreen = () => {
        const container = containerRef.current;
        if (!container) return;

        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            container.requestFullscreen?.();
        }
    };

    let cropStyles = {};
    if (crop?.croppedAreaPixels && videoDimensions.width && videoDimensions.height) {
        const { x, y, width: cropW, height: cropH } = crop.croppedAreaPixels;
        const scaleX = 100 / (cropW / videoDimensions.width);
        const scaleY = 100 / (cropH / videoDimensions.height);

        cropStyles = {
            width: `${scaleX}%`,
            height: `${scaleY}%`,
            transform: `translate(-${(x / videoDimensions.width) * 100}%, -${(y / videoDimensions.height) * 100}%)`,
            transformOrigin: "top left",
        };
    }
    const useCrop = !!(crop?.croppedAreaPixels && videoDimensions.width && videoDimensions.height);

    if (isImage) {
        return (
            <>
                <ImagePreview src={noticeMedia.getMediaAssetUrl() ?? AttachIcon} alt="Image Thumbnail" />
            </>
        );
    }

    if (isVideo) {
        const validTrimStart = trimTimeStart >= 0 ? trimTimeStart : 0;
        const progressValue = currentTime - validTrimStart;
      
        return (
            <VideoContainer
                ref={containerRef}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                    cursor: isPlaying && !isHovered ? "none" : "default",
                    overflow: useCrop ? "hidden" : "visible",
                }}
            >
                {/* After cropping display videos based on cropped area, else edit */}
                { useCrop ? (
                    <>
                        <video
                            ref={videoRef}
                            controls={false}
                            muted={false}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                ...cropStyles,
                                objectFit: "initial",
                            }}
                        >
                            <source src={noticeMedia.getMediaAssetUrl()} type="video/mp4" /> Your browser does not support the video tag.
                        </video>
                        {/* Custom Controls */}
                        <VideoBottomControls show={!isPlaying || isHovered} onClick={(e) => e.stopPropagation()}>
                            <VideoControlsBar interactive>
                                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                    <ControlButton onClick={togglePlayPause}>
                                        {isPlaying ? <PauseIcon /> : <PlayIcon />}
                                    </ControlButton>
                                    { mediaIndex === 0 && (
                                        <span
                                            style={{
                                                fontSize: "0.85rem",
                                                fontFamily: "Manrope, sans-serif",
                                            }}
                                        >
                                            {formatTime(Math.max(0, currentTime - validTrimStart))} / {formatTime(duration)}
                                        </span>
                                    )}
                                </div>
                              
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    { mediaIndex === 0 && (
                                        <ControlButton onClick={toggleMute}>
                                            <VolumeIcon muted={muted} volume={videoRef.current?.volume ?? 1} />
                                        </ControlButton>
                                    )}
                                    <ControlButton onClick={toggleFullscreen}>
                                        <FullscreenIcon />
                                    </ControlButton>
                                </div>
                            </VideoControlsBar>
                            <CustomRange
                                value={progressValue}
                                max={duration}
                                min={0}
                                step="0.1"
                                onChange={handleProgressChange}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </VideoBottomControls>
                    </>
                ) : (
                    <video
                        ref={videoRef}
                        controls
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "initial",
                            borderRadius: "12px",
                        }}
                    >
                        <source src={noticeMedia.getMediaAssetUrl()} type="video/mp4" />Your browser does not support the video tag.
                    </video>
                )}
            </VideoContainer>
        );
    }

    return (
        <>
            <ThumbnailPlaceholderPreview src={AttachIcon} alt="Attach Icon" />
        </>
    );
};